import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import Typography from '@mui/material/Typography';
import UsersList from './users';
import MainCard from 'ui-component/cards/MainCard';
import { gridSpacing } from 'store/constant';
import { IconPlus } from '@tabler/icons-react';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import CreateBritamUser from 'Application/components/Modals/CreateBritamUser';
import { useCreateUserStore, useRegionStore } from 'Application/store';
import { Autocomplete, TextField } from '@mui/material';
import { Public, Search } from '@mui/icons-material';
import { RegionItem, RegionResponse, UserRole, UsersManagementProps } from 'Application/models';
import { getUserRoleNameFromStorage } from 'Application/utils';

const UsersManagement = () => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [countrySearch, setCountrySearch] = useState<RegionItem>({ name: 'All', regionID: 1, flag: '', countryAbbreviation: '' });
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);

    const userManagementProps: UsersManagementProps = {
        searchTerm,
        rowsPerPage,
        currentPage,
        countryValue: countrySearch.name,
        searchInput,
        setSearchInput,
        setCurrentPage
    };

    const { agencyUserData } = useCreateUserStore();
    const { getRegions, regions } = useRegionStore();

    const userRole = getUserRoleNameFromStorage();

    const initialCountryValues: RegionResponse = [
        { name: 'Search by country', regionID: 0, flag: '', countryAbbreviation: '' },
        { name: 'All', regionID: 1, flag: '', countryAbbreviation: '' }
    ];
    const countryValues = regions ? [...initialCountryValues, ...regions] : initialCountryValues;

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const addUserDialog = () => {
        setIsAddUserDialogOpen(true);
    };

    const handleSearchChange = () => {
        setSearchTerm(searchInput);
        setCurrentPage(1);
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setCurrentPage(value);
    };

    const handleRowsPerPageChange = (rows: number) => {
        setRowsPerPage(rows);
        setCurrentPage(1); // Reset to first page when rows per page change
        handleClose();
    };

    const handleCountrySearchChange = (value: RegionItem) => {
        setCountrySearch(value);
        setCurrentPage(1);
    };

    useEffect(() => {
        userRole === UserRole.ROLE_ADMINISTRATOR && getRegions();
    }, []);

    useEffect(() => {
        searchInput === '' && handleSearchChange();
    }, [searchInput]);

    return (
        <>
            <MainCard
                title={
                    <>
                        <Grid container alignItems="center" spacing={gridSpacing} className="mb-4">
                            <Grid item>
                                <Typography variant="h3">Users List</Typography>
                                <Typography pt={1} variant="subtitle2">
                                    Manage users permissions and roles
                                </Typography>
                            </Grid>
                        </Grid>
                        <Grid className="justify-start sm:justify-end" container alignItems="top" spacing={gridSpacing}>
                            {userRole === UserRole.ROLE_ADMINISTRATOR && (
                                <Grid item className="w-full sm:w-1/4">
                                    <Autocomplete
                                        size="small"
                                        disableClearable
                                        options={countryValues}
                                        value={countrySearch}
                                        onChange={(_, newValue) => newValue && handleCountrySearchChange(newValue)}
                                        getOptionLabel={(option) => option.name}
                                        renderOption={(props, option) => (
                                            <MenuItem
                                                disabled={option.name === 'Search by country'}
                                                {...props}
                                                key={option.regionID}
                                                value={option.name}
                                                className={`flex gap-${option.name === 'All' ? 0 : 2} ${option.name === 'All' && '-ml-[1px]'}`}
                                            >
                                                {option.name !== 'Search by country' &&
                                                    (option.name === 'All' ? (
                                                        <InputAdornment position="start">
                                                            <Public sx={{ color: '#0369a1' }} />
                                                        </InputAdornment>
                                                    ) : (
                                                        <img
                                                            src={`data:image/png;base64, ${option.flag}`}
                                                            alt={`Flag of ${option.name}`}
                                                            width={20}
                                                            height={20}
                                                        />
                                                    ))}
                                                <Typography>{option.name}</Typography>
                                            </MenuItem>
                                        )}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            {countrySearch.name === 'All' ? (
                                                                <Public className="ml-2 -mr-2" sx={{ color: '#0369a1' }} />
                                                            ) : (
                                                                <img
                                                                    className="ml-2 -mr-2"
                                                                    src={`data:image/png;base64, ${countrySearch.flag}`}
                                                                    alt={`Flag of ${countrySearch.name}`}
                                                                    width={20}
                                                                    height={20}
                                                                />
                                                            )}
                                                        </InputAdornment>
                                                    )
                                                }}
                                                placeholder="Search by country"
                                            />
                                        )}
                                    />
                                </Grid>
                            )}
                            <Grid item className="w-full sm:w-1/4">
                                <TextField
                                    size="small"
                                    id="searchValue"
                                    label="Search by name or email"
                                    variant="outlined"
                                    value={searchInput}
                                    fullWidth
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSearchChange()}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Search className="hover:cursor-pointer" onClick={handleSearchChange} />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid item>
                                <Button disableElevation variant="contained" onClick={addUserDialog} startIcon={<IconPlus />}>
                                    Add User
                                </Button>
                            </Grid>
                        </Grid>
                    </>
                }
                content={false}
            >
                <UsersList usersManagment={userManagementProps} />
                <Grid item xs={12} sx={{ p: 3 }}>
                    <Grid container justifyContent={agencyUserData?.totalPages! > 1 ? 'space-between' : 'end'} spacing={gridSpacing}>
                        {agencyUserData && agencyUserData?.totalPages > 1 && (
                            <Grid item>
                                <Pagination
                                    count={agencyUserData?.totalPages}
                                    color="primary"
                                    page={currentPage}
                                    onChange={handlePageChange}
                                />
                            </Grid>
                        )}
                        <Grid item>
                            <Button
                                size="large"
                                sx={{ color: 'grey.900' }}
                                color="secondary"
                                endIcon={<ExpandMoreRoundedIcon />}
                                onClick={handleClick}
                            >
                                {rowsPerPage} Rows
                            </Button>
                            {anchorEl && (
                                <Menu
                                    id="menu-user-list-style1"
                                    anchorEl={anchorEl}
                                    keepMounted
                                    open={Boolean(anchorEl)}
                                    onClose={handleClose}
                                    variant="selectedMenu"
                                    anchorOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right'
                                    }}
                                    transformOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'right'
                                    }}
                                >
                                    {[10, 20, 30].map((rows) => (
                                        <MenuItem key={rows} onClick={() => handleRowsPerPageChange(rows)}>
                                            {rows} Rows
                                        </MenuItem>
                                    ))}
                                </Menu>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </MainCard>

            <CreateBritamUser isOpen={isAddUserDialogOpen} setIsOpen={setIsAddUserDialogOpen} />
        </>
    );
};

export default UsersManagement;
